import { useEffect, useState } from "react";
import { Send, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn } from "@/components/portal/ui";
import { StatusBadge } from "@/components/portal/StatusBadge";

const NEXT_STATUS = ["in_progress", "quoted", "accepted", "closed", "rejected"];

export function InquiryInbox({ canManage }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (keepId) => {
    const { data } = await api.get("/inquiries");
    setItems(data);
    if (keepId) setSelected(data.find((i) => i.id === keepId) || null);
    else if (!selected && data.length) setSelected(data[0]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (status) => {
    await api.patch(`/inquiries/${selected.id}/status`, { status });
    load(selected.id);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    await api.post(`/inquiries/${selected.id}/messages`, { message: reply });
    setReply("");
    load(selected.id);
  };

  if (loading) return <div className="text-sm py-10" style={{ color: "var(--text-muted)" }}>{t("common.loading")}</div>;

  return (
    <div className="grid md:grid-cols-[320px_1fr] gap-4 h-full" data-testid="inquiry-inbox">
      {/* list */}
      <Card className="overflow-hidden flex flex-col max-h-[70vh]">
        <div className="p-3 border-b text-xs uppercase tracking-widest font-semibold" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
          {t("dash.inbox")} · {items.length}
        </div>
        <div className="overflow-y-auto flex-1">
          {items.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-16 text-sm" style={{ color: "var(--text-muted)" }}>
              <Inbox className="h-6 w-6" /> {t("common.none")}
            </div>
          )}
          {items.map((inq) => (
            <button
              key={inq.id}
              data-testid={`inquiry-item-${inq.id}`}
              onClick={() => setSelected(inq)}
              className="w-full text-left px-4 py-3 border-b transition-colors"
              style={{
                borderColor: "var(--border)",
                background: selected?.id === inq.id ? "var(--surface-alt)" : "transparent",
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate" style={{ color: "var(--text)" }}>{inq.subject}</span>
                <StatusBadge status={inq.status} />
              </div>
              <div className="text-xs mt-1 truncate" style={{ color: "var(--text-muted)" }}>
                {canManage ? inq.customer_name : inq.provider_name} · {new Date(inq.updated_at).toLocaleDateString()}
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* detail */}
      <Card className="flex flex-col max-h-[70vh]" data-testid="inquiry-detail">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "var(--text-muted)" }}>
            {t("inbox.select")}
          </div>
        ) : (
          <>
            <div className="p-4 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-heading text-xl font-semibold" style={{ color: "var(--text)" }}>{selected.subject}</h3>
                <StatusBadge status={selected.status} />
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                {t("inbox.customer")}: {selected.customer_name} · {t("inbox.provider")}: {selected.provider_name}
              </div>
              {canManage && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {NEXT_STATUS.map((s) => (
                    <button
                      key={s}
                      data-testid={`inquiry-status-${s}`}
                      onClick={() => setStatus(s)}
                      className="text-xs px-2.5 py-1 border transition-colors hover:opacity-80"
                      style={{ borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" }}
                    >
                      {t(`status.${s}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--text-muted)" }}>{t("inbox.timeline")}</div>
              {(selected.timeline || []).map((e, i) => (
                <div key={i} className={`flex ${e.by === "provider" ? "justify-end" : "justify-start"}`}>
                  <div
                    className="max-w-[75%] px-3.5 py-2 text-sm"
                    style={{
                      borderRadius: "var(--radius)",
                      background: e.by === "provider" ? "var(--primary)" : "var(--surface-alt)",
                      color: e.by === "provider" ? "var(--primary-fg)" : "var(--text)",
                    }}
                  >
                    {e.type !== "message" && <span className="text-[10px] uppercase tracking-widest opacity-70 block mb-0.5">{e.type}</span>}
                    {e.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t flex gap-2" style={{ borderColor: "var(--border)" }}>
              <input
                data-testid="inquiry-reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendReply()}
                placeholder={t("inbox.reply")}
                className="flex-1 px-3.5 h-11 text-sm border outline-none"
                style={{ background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" }}
              />
              <Btn onClick={sendReply} data-testid="inquiry-reply-send">
                <Send className="h-4 w-4" />
              </Btn>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
